import { useState } from 'react';
import { Button, Field } from '@mailflow/ui';
import { api } from '../lib/api.js';

interface CreateDealModalProps {
  contactIds: string[];
  contactEmail?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateDealModal({
  contactIds,
  contactEmail,
  onClose,
  onSuccess,
}: CreateDealModalProps) {
  const [title, setTitle] = useState(
    contactEmail ? `Rezensionsfall: ${contactEmail}` : `Rezensionsfall (${contactIds.length} Kontakte)`,
  );
  const [value, setValue] = useState<number>(500);
  const [status, setStatus] = useState<'won' | 'open'>('won');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    try {
      setLoading(true);
      await api.post('/api/crm/deals', {
        title: title.trim(),
        value: Number(value) || 0,
        contactIds,
      });

      onSuccess();
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Rezensionsfall konnte nicht angelegt werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" style={{ maxWidth: '500px', width: '90vw' }} role="dialog">
        <form onSubmit={(e) => void handleSubmit(e)} className="card__body stack">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>
              💼 Rezensionsfall {status === 'won' ? 'übernommen / gelöst' : 'anlegen'}
            </h2>
            <Button variant="ghost" onClick={onClose} type="button">
              ✕
            </Button>
          </div>

          <Field
            label="Bezeichnung / Referenz"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Google Rezensionsfall - Löschung / Bearbeitung"
            required
          />

          <Field
            label="Streitwert / Fallwert (CHF / EUR)"
            type="number"
            value={value.toString()}
            onChange={(e) => setValue(Number(e.target.value))}
            min="0"
          />

          <div className="field">
            <label className="field__label">Status des Rezensionsfalls</label>
            <div className="grid-2" style={{ marginTop: '4px' }}>
              <label
                className={`card card--clickable ${status === 'won' ? 'card--active' : ''}`}
                style={{ padding: '10px', cursor: 'pointer', borderColor: status === 'won' ? '#10b981' : undefined }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="dealStatus"
                    value="won"
                    checked={status === 'won'}
                    onChange={() => setStatus('won')}
                  />
                  <div>
                    <strong style={{ color: '#10b981' }}>🟢 Gewonnen / Auftrag erhalten</strong>
                    <div className="text-xs muted">Sperrt Empfänger automatisch für weitere Kampagnen-Mails</div>
                  </div>
                </div>
              </label>

              <label
                className={`card card--clickable ${status === 'open' ? 'card--active' : ''}`}
                style={{ padding: '10px', cursor: 'pointer', borderColor: status === 'open' ? '#f59e0b' : undefined }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="dealStatus"
                    value="open"
                    checked={status === 'open'}
                    onChange={() => setStatus('open')}
                  />
                  <div>
                    <strong style={{ color: '#f59e0b' }}>🟠 In Bearbeitung / Prüfphase</strong>
                    <div className="text-xs muted">Fall in Prüfung, noch nicht abgeschlossen</div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="dialog__actions" style={{ marginTop: '12px' }}>
            <Button variant="secondary" onClick={onClose} type="button">
              Abbrechen
            </Button>
            <Button type="submit" loading={loading}>
              Rezensionsfall speichern
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
